import { mapAuditExportFields } from '../../../lib/auditColumnDefs';
import { GRID_LIST_FETCH_SIZE } from '../../../components/shared/agGridCells';
import { exportRowsToExcel } from '../../../utils/excelExport';
import type { StorefrontUrlSubmissionRow } from '../../storefront-urls/models/types';
import { STOREFRONT_URL_REVIEW_EXPORT_COLUMNS } from '../constants/constants';
import type {
  StorefrontUrlReviewColumnFilterKey,
  StorefrontUrlReviewColumnFilters,
  StorefrontUrlReviewListRequest,
} from '../models/types';

export async function exportStorefrontUrlReviewExcel(rows: StorefrontUrlSubmissionRow[]) {
  await exportRowsToExcel(
    rows.map((row) => ({
      merchantName: row.merchantName,
      url: row.url,
      rejectionReason: row.rejectionReason ?? '',
      ...mapAuditExportFields(row),
      status: row.status,
    })),
    STOREFRONT_URL_REVIEW_EXPORT_COLUMNS,
    {
      sheetName: 'URL Approvals',
      fileNamePrefix: 'url-approvals',
    },
  );
}

const STOREFRONT_URL_REVIEW_COLUMN_FILTER_KEYS: Record<StorefrontUrlReviewColumnFilterKey, true> = {
  merchantName: true,
  url: true,
  status: true,
  rejectionReason: true,
  createdAt: true,
};

type AgGridSimpleFilterModel = {
  filter?: unknown;
  dateFrom?: string;
  dateTo?: string;
  conditions?: Array<{
    filter?: unknown;
    dateFrom?: string;
    dateTo?: string;
  }>;
};

function firstCondition(model: AgGridSimpleFilterModel): AgGridSimpleFilterModel {
  return model.conditions?.[0] ?? model;
}

/**
 * Translate AG Grid's native column filterModel into the same shape the Filters drawer
 * stores in `columnFilters` / feeds `buildStorefrontUrlReviewRequest` — so header-funnel
 * filters and the drawer both produce identical `request.filters` payloads.
 *
 * Text filters → `{ [colId]: [filterText] }` (drawer multi-select uses string[] too).
 * Date column (`agDateColumnFilter` on createdAt) → `createdAt: [from, to]` yyyy-MM-dd.
 */
export function mapAgGridFilterModelToStorefrontUrlReviewFilters(
  filterModel: Record<string, unknown> | null | undefined,
): Partial<StorefrontUrlReviewColumnFilters> {
  const columnFilters: Partial<StorefrontUrlReviewColumnFilters> = {};
  if (!filterModel) return columnFilters;

  for (const [colId, raw] of Object.entries(filterModel)) {
    if (!raw || typeof raw !== 'object') continue;
    const model = firstCondition(raw as AgGridSimpleFilterModel);

    if (colId === 'createdAt') {
      if (typeof model.dateFrom === 'string' && model.dateFrom.length >= 10) {
        const from = model.dateFrom.slice(0, 10);
        const to = (model.dateTo ?? model.dateFrom).slice(0, 10);
        columnFilters.createdAt = [from, to];
      }
      continue;
    }

    if (typeof model.filter !== 'string' || !model.filter.trim()) continue;
    if (colId in STOREFRONT_URL_REVIEW_COLUMN_FILTER_KEYS) {
      columnFilters[colId as StorefrontUrlReviewColumnFilterKey] = [model.filter];
    }
  }

  return columnFilters;
}

export function buildStorefrontUrlReviewRequest(options: {
  page?: number;
  size?: number;
  filterStatus: string;
  search: string;
  columnFilters: StorefrontUrlReviewColumnFilters;
  export?: boolean;
}): StorefrontUrlReviewListRequest {
  const request: StorefrontUrlReviewListRequest = {
    page: options.page ?? 0,
    size: options.size ?? GRID_LIST_FETCH_SIZE,
  };

  // Column/drawer `status` wins over the tab so they are not ANDed
  // (Pending tab + Approved column filter would otherwise return nothing).
  if (options.filterStatus !== 'All' && !options.columnFilters.status?.length) {
    request.status = options.filterStatus.toUpperCase();
  }

  const query = options.search.trim();
  if (query) request.search = query;

  const filters = Object.fromEntries(
    (
      Object.entries(options.columnFilters) as [
        StorefrontUrlReviewColumnFilterKey,
        string[] | null,
      ][]
    )
      .filter(([, values]) => Boolean(values?.length))
      .map(([key, values]) => [key, values as string[]]),
  );

  if (Object.keys(filters).length > 0) request.filters = filters;
  if (options.export) request.export = true;

  return request;
}

function storefrontUrlReviewColumnFilterCell(
  row: StorefrontUrlSubmissionRow,
  key: StorefrontUrlReviewColumnFilterKey,
): string {
  switch (key) {
    case 'merchantName':
      return row.merchantName || '—';
    case 'url':
      return row.url || '—';
    case 'status':
      return row.status || 'UNKNOWN';
    case 'rejectionReason':
      return row.rejectionReason || '—';
    case 'createdAt':
      return row.createdAt ?? '';
    default:
      return '';
  }
}

export function filterStorefrontUrlReviewRows(
  rows: StorefrontUrlSubmissionRow[],
  options: {
    filterStatus: string;
    search: string;
    columnFilters: StorefrontUrlReviewColumnFilters;
  },
): StorefrontUrlSubmissionRow[] {
  const query = options.search.trim().toLowerCase();
  const tabStatus =
    !options.columnFilters.status?.length && options.filterStatus !== 'All'
      ? options.filterStatus.toLowerCase()
      : null;

  return rows.filter((row) => {
    if (tabStatus && row.status.toLowerCase() !== tabStatus) return false;

    if (query) {
      const haystack = [row.merchantName, row.url, row.status, row.rejectionReason ?? '']
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(query)) return false;
    }

    for (const key of Object.keys(STOREFRONT_URL_REVIEW_COLUMN_FILTER_KEYS) as StorefrontUrlReviewColumnFilterKey[]) {
      const values = options.columnFilters[key];
      if (!values?.length) continue;

      if (key === 'createdAt') {
        const from = values[0];
        const to = values[1] ?? values[0];
        const d = row.createdAt?.slice(0, 10) ?? '';
        if (!d || (from && d < from) || (to && d > to)) return false;
        continue;
      }

      const cell = storefrontUrlReviewColumnFilterCell(row, key).toLowerCase();
      if (!values.some((value) => cell.includes(value.trim().toLowerCase()))) return false;
    }

    return true;
  });
}
